/**
 * InlineChart — renders plot data from chat tools as a Highcharts chart.
 * Detects plotType and renders the matching chart. Supports the original
 * Direct-Shear plots (line / mohr_coulomb / summary_bar) plus the scheduling
 * chart specs (timeline / utilization / heatmap / allocation / trend).
 * All colours come from theme tokens (theme.chart / theme.schedule) — no hex
 * literals in this file.
 */
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HC_xrange from 'highcharts/modules/xrange';
import HC_heatmap from 'highcharts/modules/heatmap';

// Initialise the extra modules once on this Highcharts instance (defensive —
// some HC12 module default exports are functions, others pre-initialised).
const initIfFunction = (mod: unknown): void => {
  const fn = mod as (hc: typeof Highcharts) => unknown;
  if (typeof fn === 'function') fn(Highcharts);
};
initIfFunction(HC_xrange);
initIfFunction(HC_heatmap);

interface PlotData {
  plotType: string;
  title?: string;
  xLabel?: string;
  yLabel?: string;
  series?: Array<{ stage?: number; label?: string; name?: string; data?: Array<{ x: number; y: number }>; values?: number[] }>;
  stages?: Array<Record<string, number>>;
  // scheduling specs
  categories?: string[];
  points?: Array<{ start: string; end: string; row: number; label: string; color?: string }>;
  values?: number[];
  xCategories?: string[];
  yCategories?: string[];
  cells?: Array<{ x: number; y: number; value: number }>;
  slices?: Array<{ name: string; value: number }>;
}

/** Try to parse plot data from a tool output string. */
export function parsePlotData(toolOutput: string): PlotData | null {
  try {
    const parsed = JSON.parse(toolOutput);
    const hasData = parsed.series || parsed.stages || parsed.points || parsed.values || parsed.cells || parsed.slices;
    // Scheduling tools wrap the spec under `chart`.
    if (parsed.chart && parsed.chart.plotType) return parsed.chart as PlotData;
    if (parsed.plotType && hasData) return parsed as PlotData;
  } catch { /* not plot data */ }
  return null;
}

const DAY = 86_400_000;
function dateMs(iso: string): number { return Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))); }
function utilColor(v: number, t: Theme): string {
  return v > 100 ? t.schedule.heatmapHigh : v > 70 ? t.schedule.heatmapMid : t.schedule.committed;
}

export const InlineChart = ({ data }: { data: PlotData }) => {
  const theme = useTheme();
  const options = useMemo((): Highcharts.Options => {
    switch (data.plotType) {
      case 'timeline': return buildTimeline(data, theme);
      case 'utilization': return buildUtilization(data, theme);
      case 'heatmap': return buildHeatmap(data, theme);
      case 'allocation': return buildPie(data, theme);
      case 'trend': return buildTrend(data, theme);
      case 'mohr_coulomb': case 'summary_bar': return buildBarChart(data, theme);
      default: return buildLineChart(data, theme);
    }
  }, [data, theme]);

  return (
    <Box sx={{ my: 1, borderRadius: 1, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
};

// ── scheduling builders ─────────────────────────────────────────────────────
function buildTimeline(d: PlotData, t: Theme): Highcharts.Options {
  const cats = d.categories || [];
  const points = (d.points || []).map((p) => ({ x: dateMs(p.start), x2: dateMs(p.end) + DAY, y: p.row, name: p.label, color: p.color }));
  return {
    chart: { type: 'xrange', height: Math.max(170, cats.length * 34 + 90), style: { fontFamily: t.typography.fontFamily } },
    title: { text: d.title || 'Schedule', style: { fontSize: '13px' } },
    xAxis: { type: 'datetime', gridLineWidth: 1, gridLineColor: t.chart.gridLine },
    yAxis: { categories: cats, title: { text: undefined }, reversed: true },
    credits: { enabled: false }, legend: { enabled: false },
    tooltip: { headerFormat: '', pointFormat: '<b>{point.name}</b>' },
    series: [{
      type: 'xrange', name: 'Schedule', borderColor: alpha(t.palette.common.black, 0.15), pointWidth: 16,
      data: points as unknown as Highcharts.SeriesXrangeOptions['data'],
      dataLabels: { enabled: true, formatter(this: { point?: { name?: string } }) { return this.point?.name ?? ''; }, style: { fontSize: '9px', textOutline: 'none', color: t.palette.common.white } },
    } as Highcharts.SeriesXrangeOptions],
  };
}

function buildUtilization(d: PlotData, t: Theme): Highcharts.Options {
  const values = d.values || [];
  return {
    chart: { type: 'column', height: 300, style: { fontFamily: t.typography.fontFamily } },
    title: { text: d.title || 'Utilization', style: { fontSize: '13px' } },
    xAxis: { categories: d.categories || [], labels: { style: { fontSize: '10px' } } },
    yAxis: { title: { text: d.yLabel || '%' }, max: Math.max(100, ...values, 0) },
    credits: { enabled: false }, legend: { enabled: false },
    series: [{ type: 'column', name: d.yLabel || '%', data: values.map((v) => ({ y: v, color: utilColor(v, t) })) }],
  };
}

function buildHeatmap(d: PlotData, t: Theme): Highcharts.Options {
  const cells = (d.cells || []).map((c) => [c.x, c.y, c.value]);
  return {
    chart: { type: 'heatmap', height: Math.max(190, (d.yCategories?.length || 1) * 26 + 120), style: { fontFamily: t.typography.fontFamily } },
    title: { text: d.title || 'Capacity', style: { fontSize: '13px' } },
    xAxis: { categories: d.xCategories || [], labels: { style: { fontSize: '9px' } } },
    yAxis: { categories: d.yCategories || [], title: { text: undefined }, reversed: true, labels: { style: { fontSize: '10px' } } },
    colorAxis: { min: 0, max: 120, stops: [[0, t.chart.heatLow], [0.58, t.chart.heatMid], [0.84, t.schedule.skillLevel3], [1, t.schedule.heatmapHigh]] },
    credits: { enabled: false }, legend: { enabled: false },
    tooltip: { pointFormat: '<b>{point.value}%</b>' },
    series: [{
      type: 'heatmap', borderWidth: 1, borderColor: t.palette.background.paper, data: cells,
      dataLabels: { enabled: true, formatter(this: { point?: { value?: number } }) { const v = this.point?.value; return v ? `${v}%` : ''; }, style: { fontSize: '9px', textOutline: 'none' } },
    } as Highcharts.SeriesHeatmapOptions],
  };
}

function buildPie(d: PlotData, t: Theme): Highcharts.Options {
  return {
    chart: { type: 'pie', height: 300, style: { fontFamily: t.typography.fontFamily } },
    title: { text: d.title || 'Allocation', style: { fontSize: '13px' } },
    credits: { enabled: false },
    colors: t.chart.series,
    series: [{ type: 'pie', name: 'Share', innerSize: '55%', data: (d.slices || []).map((s) => ({ name: s.name, y: s.value })), dataLabels: { style: { fontSize: '10px' } } }],
  };
}

function buildTrend(d: PlotData, t: Theme): Highcharts.Options {
  return {
    chart: { type: 'line', height: 300, style: { fontFamily: t.typography.fontFamily } },
    title: { text: d.title || 'Trend', style: { fontSize: '13px' } },
    xAxis: { categories: d.categories || [] },
    yAxis: { title: { text: d.yLabel || '' } },
    credits: { enabled: false }, legend: { enabled: (d.series?.length || 0) > 1 },
    series: (d.series || []).map((s, i) => ({ type: 'line' as const, name: s.name || s.label || 'Series', color: t.chart.series[i % t.chart.series.length], data: s.values || [] })),
  };
}

// ── original Direct-Shear builders ──────────────────────────────────────────
function buildLineChart(data: PlotData, t: Theme): Highcharts.Options {
  const colors = t.chart.series;
  return {
    chart: { type: 'line', height: 320, style: { fontFamily: t.typography.fontFamily } },
    title: { text: data.title || `${data.yLabel} vs ${data.xLabel}`, style: { fontSize: '13px' } },
    xAxis: { title: { text: data.xLabel || 'X' }, gridLineWidth: 1, gridLineColor: t.chart.gridLine },
    yAxis: { title: { text: data.yLabel || 'Y' }, gridLineColor: t.chart.gridLine },
    legend: { enabled: (data.series?.length || 0) > 1 },
    credits: { enabled: false },
    plotOptions: { line: { marker: { enabled: false }, lineWidth: 2 } },
    series: (data.series || []).map((s, i) => ({
      type: 'line' as const, name: s.label || `Series ${i + 1}`, color: colors[i % colors.length],
      data: (s.data || []).map((p) => [p.x, p.y]),
    })),
  };
}

function buildBarChart(data: PlotData, t: Theme): Highcharts.Options {
  const stages = data.stages || [];
  return {
    chart: { type: 'column', height: 280, style: { fontFamily: t.typography.fontFamily } },
    title: { text: data.title || 'Stage Summary', style: { fontSize: '13px' } },
    xAxis: { categories: stages.map((_, i) => `Stage ${i + 1}`), title: { text: data.xLabel } },
    yAxis: { title: { text: data.yLabel || 'Value' } },
    credits: { enabled: false },
    series: [
      { type: 'column' as const, name: 'Peak Shear Stress', color: t.chart.series[0], data: stages.map((s) => s.PeakShearStress || 0) },
      { type: 'column' as const, name: 'Residual Shear Stress', color: t.chart.series[1], data: stages.map((s) => s.ResidualShearStress || 0) },
    ],
  };
}
